import { useState, useRef, useCallback, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { SocialNetwork, PlatformPostCounts, KIE_MODELS, ContentCategory } from '@agency-smm/shared';
import { PLATFORM_LIST } from '../utils/networkIcons';
import { ExportQuality, EXPORT_PRESETS } from '../utils/canvasRenderer';

// ─── Animations ─────────────────────────────────────────────────────────────

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to { opacity: 1; transform: translateY(0); }
`;

const dotBounce = keyframes`
  0%, 80%, 100% { transform: scale(0.6); opacity: 0.4; }
  40% { transform: scale(1); opacity: 1; }
`;

// ─── Chat Styled Components ─────────────────────────────────────────────────

const ChatContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 60vh;
  overflow-y: auto;
  padding: 4px 2px;
  scroll-behavior: smooth;
  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-thumb { background: ${({ theme }) => theme.colors.border}; border-radius: 2px; }
`;

const MsgRow = styled.div<{ $from: 'novah' | 'user' }>`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  justify-content: ${({ $from }) => ($from === 'user' ? 'flex-end' : 'flex-start')};
  animation: ${fadeIn} 0.3s ease-out;
`;

const Avatar = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  box-shadow: 0 1px 4px rgba(59, 130, 246, 0.25);
  img { width: 100%; height: 100%; object-fit: cover; }
`;

const Bubble = styled.div<{ $from: 'novah' | 'user' }>`
  max-width: 85%;
  padding: 8px 12px;
  border-radius: ${({ $from }) => ($from === 'novah' ? '4px 12px 12px 12px' : '12px 4px 12px 12px')};
  font-size: 13px;
  line-height: 1.5;
  background: ${({ $from, theme }) => ($from === 'novah' ? theme.colors.background : theme.colors.primary)};
  color: ${({ $from, theme }) => ($from === 'novah' ? theme.colors.text : '#fff')};
  border: ${({ $from, theme }) => ($from === 'novah' ? theme.borders.thin : 'none')};
  strong { font-weight: 600; }
`;

const TypingDots = styled.div`
  display: flex;
  gap: 4px;
  padding: 6px 4px;
  span {
    width: 6px; height: 6px; border-radius: 50%;
    background: ${({ theme }) => theme.colors.textMuted};
    animation: ${dotBounce} 1.2s infinite;
    &:nth-child(2) { animation-delay: 0.15s; }
    &:nth-child(3) { animation-delay: 0.3s; }
  }
`;

// ─── Widget Styled Components ───────────────────────────────────────────────

const WidgetWrap = styled.div`
  animation: ${fadeIn} 0.3s ease-out;
  margin: 4px 0;
`;

const PlatformGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 6px;
`;

const PlatformBtn = styled.button<{ $active: boolean }>`
  padding: 8px 10px;
  border: 1px solid ${({ $active, theme }) => ($active ? theme.colors.primary : theme.colors.border)};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ $active, theme }) => ($active ? theme.colors.primary : theme.colors.surface)};
  color: ${({ $active, theme }) => ($active ? '#fff' : theme.colors.text)};
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;
  &:hover { border-color: ${({ theme }) => theme.colors.primary}; }
`;

const ConfirmBtn = styled.button`
  width: 100%;
  padding: 8px 12px;
  margin-top: 6px;
  background: ${({ theme }) => theme.colors.primary};
  color: #fff;
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  &:hover { background: ${({ theme }) => theme.colors.primaryHover}; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const CountGrid = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
`;

const CountBtn = styled.button<{ $active: boolean }>`
  padding: 6px 16px;
  border: 1px solid ${({ $active, theme }) => ($active ? theme.colors.primary : theme.colors.border)};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  background: ${({ $active, theme }) => ($active ? theme.colors.primary : theme.colors.surface)};
  color: ${({ $active, theme }) => ($active ? '#fff' : theme.colors.text)};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  &:hover { border-color: ${({ theme }) => theme.colors.primary}; }
`;

const PromptInput = styled.textarea`
  width: 100%;
  padding: 8px 10px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: 13px;
  font-family: inherit;
  resize: none;
  min-height: 50px;
  &:focus { outline: none; border-color: ${({ theme }) => theme.colors.primary}; }
`;

const SkipBtn = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 12px;
  cursor: pointer;
  margin-top: 4px;
  &:hover { color: ${({ theme }) => theme.colors.primary}; }
`;

const BackBtn = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 12px;
  cursor: pointer;
  padding: 4px 0;
  &:hover { color: ${({ theme }) => theme.colors.primary}; }
`;

const StyleOption = styled.button<{ $active: boolean; $locked?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 10px 8px;
  border: 1px solid ${({ $active, $locked, theme }) => ($locked ? theme.colors.border : $active ? theme.colors.primary : theme.colors.border)};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ $active, $locked, theme }) => ($locked ? theme.colors.background : $active ? 'rgba(59,130,246,0.08)' : theme.colors.surface)};
  cursor: ${({ $locked }) => ($locked ? 'pointer' : 'pointer')};
  transition: all 0.15s ease;
  opacity: ${({ $locked }) => ($locked ? 0.55 : 1)};
  position: relative;
  &:hover { border-color: ${({ $locked, theme }) => ($locked ? theme.colors.border : theme.colors.primary)}; }
`;

const LockBadge = styled.span`
  position: absolute;
  top: 4px;
  right: 4px;
  font-size: 10px;
  background: rgba(0,0,0,0.08);
  border-radius: 8px;
  padding: 1px 5px;
  color: ${({ theme }) => theme.colors.textMuted};
  font-weight: 700;
`;

const PlanTag = styled.span`
  font-size: 9px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.textMuted};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-top: 2px;
`;

const StyleGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 6px;
`;

const StyleIcon = styled.span`
  font-size: 20px;
  margin-bottom: 4px;
`;

const StyleLabel = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
`;

const SummaryCard = styled.div`
  background: ${({ theme }) => theme.colors.background};
  border: ${({ theme }) => theme.borders.thin};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: 10px 12px;
  font-size: 12px;
  line-height: 1.7;
  color: ${({ theme }) => theme.colors.textSecondary};
  strong { color: ${({ theme }) => theme.colors.text}; }
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 6px;
  background: ${({ theme }) => theme.colors.border};
  border-radius: 3px;
  overflow: hidden;
  margin: 6px 0;
`;

const ProgressFill = styled.div<{ $pct: number }>`
  width: ${({ $pct }) => $pct}%;
  height: 100%;
  background: ${({ theme }) => theme.colors.primary};
  transition: width 0.3s ease;
`;

const ActionRow = styled.div`
  display: flex;
  gap: 6px;
  margin-top: 4px;
  > * { flex: 1; }
`;

const OutlineBtn = styled.button`
  padding: 7px 10px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.text};
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  &:hover { border-color: ${({ theme }) => theme.colors.primary}; color: ${({ theme }) => theme.colors.primary}; }
`;

const QualityRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 4px;
`;

const QualityLabel = styled.label`
  font-size: 11px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textSecondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const QualitySelect = styled.select`
  padding: 7px 10px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.text};
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  width: 100%;
  &:focus { outline: none; border-color: ${({ theme }) => theme.colors.primary}; }
`;

// ─── Types ──────────────────────────────────────────────────────────────────

interface ChatMsg {
  id: number;
  from: 'novah' | 'user';
  text: string;
  widget?: string;
}

type Step = 'platforms' | 'count' | 'prompt' | 'content-type' | 'style' | 'ai-model' | 'confirm' | 'generating' | 'done';

const PLATFORMS = PLATFORM_LIST;

type ImageStyle = 'stock' | 'pastel' | 'asset' | 'ai' | 'mix';

type ContentTypeChoice = ContentCategory | 'mix';

const CONTENT_TYPE_OPTIONS: { id: ContentTypeChoice; label: string; icon: string; hint: string }[] = [
  { id: 'education', label: 'Education', icon: '📚', hint: 'Tips & how-tos' },
  { id: 'connection', label: 'Connection', icon: '💬', hint: 'Community & stories' },
  { id: 'proof', label: 'Proof', icon: '🏆', hint: 'Testimonials & results' },
  { id: 'promotion', label: 'Promotion', icon: '🔥', hint: 'Sales & CTAs' },
  { id: 'mix', label: 'Mix (all)', icon: '✨', hint: 'Balanced variety' },
];

export type UserPlan = 'free' | 'pro' | 'business';

// ─── Props ──────────────────────────────────────────────────────────────────

interface NovahGenerateChatProps {
  workspaceName: string;
  credits: number;
  hasBackgroundImages: boolean;
  userPlan: UserPlan;
  onNavigateBilling: () => void;
  onGenerate: (config: {
    platformCounts: PlatformPostCounts;
    customPrompt: string;
    imageStyle: ImageStyle;
    contentType?: ContentTypeChoice;
    kieModel?: string;
  }) => void;
  isStreaming: boolean;
  streamProgress: { current: number; total: number };
  onExportCSV: (quality: ExportQuality) => void;
  onDownloadZIP: (quality: ExportQuality) => void;
  postsCount: number;
}

// ─── Component ──────────────────────────────────────────────────────────────

export function NovahGenerateChat({
  workspaceName,
  credits,
  hasBackgroundImages,
  userPlan,
  onNavigateBilling,
  onGenerate,
  isStreaming,
  streamProgress,
  onExportCSV,
  onDownloadZIP,
  postsCount,
}: NovahGenerateChatProps) {
  const [messages, setMessages] = useState<ChatMsg[]>([]);
  const [typing, setTyping] = useState(false);
  const [step, setStep] = useState<Step>('platforms');
  const [stepHistory, setStepHistory] = useState<Step[]>([]);
  const msgIdRef = useRef(0);
  const scrollRef = useRef<HTMLDivElement>(null);
  const hasStarted = useRef(false);

  // Collected data
  const [selectedPlatforms, setSelectedPlatforms] = useState<Set<SocialNetwork>>(new Set());
  const [countPerPlatform, setCountPerPlatform] = useState(2);
  const [customPrompt, setCustomPrompt] = useState('');
  const [imageStyle, setImageStyle] = useState<ImageStyle>('mix');
  const [contentType, setContentType] = useState<ContentTypeChoice>('mix');
  const [kieModel, setKieModel] = useState<string | undefined>(undefined);
  const [exportQuality, setExportQuality] = useState<ExportQuality>('mobile');

  // Rotating status messages during generation
  const GEN_STATUS_MESSAGES = [
    'Analyzing your brand voice...',
    'Crafting compelling headlines...',
    'Selecting the perfect images...',
    'Writing engaging copy...',
    'Matching templates to content...',
    'Generating dynamic text overlays...',
    'Fine-tuning your CTAs...',
    'Building your content mix...',
    'Almost there, polishing final details...',
    'Putting the finishing touches...',
  ];
  const [genStatusIdx, setGenStatusIdx] = useState(0);

  useEffect(() => {
    if (step !== 'generating') {
      setGenStatusIdx(0);
      return;
    }
    const interval = setInterval(() => {
      setGenStatusIdx(prev => (prev + 1) % GEN_STATUS_MESSAGES.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [step]);

  const pushStep = (next: Step) => {
    setStepHistory(prev => [...prev, step]);
    setStep(next);
  };

  const goBack = () => {
    if (stepHistory.length === 0) return;
    const prev = stepHistory[stepHistory.length - 1];
    setStepHistory(h => h.slice(0, -1));
    setStep(prev);
  };

  const nextId = () => ++msgIdRef.current;
  const scroll = useCallback(() => {
    setTimeout(() => scrollRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' }), 80);
  }, []);

  const addNovah = useCallback((text: string, widget?: string): Promise<void> => {
    return new Promise((resolve) => {
      setTyping(true);
      scroll();
      setTimeout(() => {
        setTyping(false);
        setMessages((prev) => [...prev, { id: nextId(), from: 'novah', text, widget }]);
        scroll();
        resolve();
      }, 500);
    });
  }, [scroll]);

  const addUser = useCallback((text: string) => {
    setMessages((prev) => [...prev, { id: nextId(), from: 'user', text }]);
    scroll();
  }, [scroll]);

  // Intro
  useEffect(() => {
    if (hasStarted.current) return;
    hasStarted.current = true;
    const run = async () => {
      await new Promise((r) => setTimeout(r, 300));
      await addNovah(`Ready to create content for **${workspaceName}**? Pick your platforms.`, 'platforms');
    };
    run();
  }, [addNovah, workspaceName]);

  // Transition to done when streaming finishes with posts
  useEffect(() => {
    if (step === 'generating' && !isStreaming && postsCount > 0) {
      addNovah(`Done! ${postsCount} posts ready. Tap any post to preview or download.`, 'done');
      setStep('done');
    }
  }, [isStreaming, postsCount, step, addNovah]);

  // ─── Step Handlers ──────────────────────────────────────────────────────

  const confirmPlatforms = async () => {
    if (selectedPlatforms.size === 0) return;
    const names = Array.from(selectedPlatforms).map((p) => PLATFORMS.find((x) => x.id === p)?.label).join(', ');
    addUser(names);
    await addNovah('How many posts per platform?', 'count');
    pushStep('count');
  };

  const confirmCount = async (n: number) => {
    setCountPerPlatform(n);
    const total = selectedPlatforms.size * n;
    addUser(`${n} per platform (${total} total)`);
    await addNovah('Any specific topic or angle? Or let me surprise you.', 'prompt');
    pushStep('prompt');
  };

  const confirmPrompt = async (text: string) => {
    setCustomPrompt(text);
    addUser(text || 'Surprise me');
    await addNovah('What type of content should I create?', 'content-type');
    pushStep('content-type');
  };

  const confirmContentType = async (choice: ContentTypeChoice) => {
    setContentType(choice);
    const label = CONTENT_TYPE_OPTIONS.find(o => o.id === choice)?.label || 'Mix';
    addUser(label);
    await addNovah('What kind of images should I use?', 'style');
    pushStep('style');
  };

  const confirmStyle = async (style: ImageStyle) => {
    setImageStyle(style);
    const labels: Record<ImageStyle, string> = {
      stock: 'Stock photos',
      pastel: 'Solid colors',
      asset: 'My own photos',
      ai: 'AI Generated',
      mix: 'Mix (stock + solid)',
    };
    addUser(labels[style]);

    // Business users choosing AI or Mix get to pick the AI model
    const needsModelPick = userPlan === 'business' && (style === 'ai' || style === 'mix');
    if (needsModelPick) {
      await addNovah('Which AI model should I use for your images?', 'ai-model');
      pushStep('ai-model');
    } else {
      await addNovah("Here's the plan:", 'confirm');
      pushStep('confirm');
    }
  };

  const confirmAiModel = async (model: string) => {
    setKieModel(model);
    const modelLabel = model === 'mix'
      ? 'Mix all models'
      : KIE_MODELS.find(m => m.id === model)?.name || model;
    addUser(modelLabel);
    await addNovah("Here's the plan:", 'confirm');
    pushStep('confirm');
  };

  const triggerGenerate = () => {
    const platformCounts: PlatformPostCounts = {};
    selectedPlatforms.forEach((p) => { platformCounts[p] = countPerPlatform; });
    const total = selectedPlatforms.size * countPerPlatform;
    addUser(`Generate ${total} posts`);
    setStep('generating');
    onGenerate({ platformCounts, customPrompt, imageStyle, contentType, kieModel });
  };

  const resetFlow = async () => {
    setSelectedPlatforms(new Set());
    setCountPerPlatform(2);
    setCustomPrompt('');
    setImageStyle('mix');
    setContentType('mix');
    setMessages([]);
    setStep('platforms');
    setStepHistory([]);
    hasStarted.current = false;
    msgIdRef.current = 0;
    await new Promise((r) => setTimeout(r, 100));
    hasStarted.current = true;
    await addNovah(`Ready to create more content for **${workspaceName}**? Pick your platforms.`, 'platforms');
  };

  // ─── Total count helper ─────────────────────────────────────────────────
  const totalPosts = selectedPlatforms.size * countPerPlatform;

  // ─── Render ─────────────────────────────────────────────────────────────

  return (
    <div>
      <ChatContainer>
        {messages.map((msg) => (
          <div key={msg.id}>
            <MsgRow $from={msg.from}>
              {msg.from === 'novah' && <Avatar><img src="/novah_laugh.png" alt="Novah" /></Avatar>}
              <Bubble $from={msg.from}>{msg.text.split('**').map((part, i) => i % 2 === 1 ? <strong key={i}>{part}</strong> : part)}</Bubble>
            </MsgRow>

            {/* Widgets render after their novah message */}
            {msg.from === 'novah' && msg.widget === 'platforms' && step === 'platforms' && (
              <WidgetWrap>
                <PlatformGrid>
                  {PLATFORMS.map((p) => (
                    <PlatformBtn
                      key={p.id}
                      $active={selectedPlatforms.has(p.id)}
                      onClick={() => setSelectedPlatforms((prev) => {
                        const next = new Set(prev);
                        next.has(p.id) ? next.delete(p.id) : next.add(p.id);
                        return next;
                      })}
                    >
                      {p.icon} {p.label}
                    </PlatformBtn>
                  ))}
                </PlatformGrid>
                <ConfirmBtn onClick={confirmPlatforms} disabled={selectedPlatforms.size === 0}>
                  Continue with {selectedPlatforms.size} platform{selectedPlatforms.size !== 1 ? 's' : ''}
                </ConfirmBtn>
              </WidgetWrap>
            )}

            {msg.from === 'novah' && msg.widget === 'count' && step === 'count' && (
              <WidgetWrap>
                <CountGrid>
                  {[1, 2, 3, 5, 10].map((n) => (
                    <CountBtn key={n} $active={false} onClick={() => confirmCount(n)}>
                      {n}
                    </CountBtn>
                  ))}
                </CountGrid>
              </WidgetWrap>
            )}

            {msg.from === 'novah' && msg.widget === 'prompt' && step === 'prompt' && (
              <WidgetWrap>
                <PromptInput
                  placeholder="Summer collection launch, new product..."
                  value={customPrompt}
                  onChange={(e) => setCustomPrompt(e.target.value)}
                  rows={2}
                />
                <ConfirmBtn onClick={() => confirmPrompt(customPrompt)} style={{ marginTop: 4 }}>
                  {customPrompt.trim() ? 'Continue' : 'Skip'}
                </ConfirmBtn>
                {customPrompt.trim() && (
                  <SkipBtn onClick={() => confirmPrompt('')}>Skip — surprise me</SkipBtn>
                )}
              </WidgetWrap>
            )}

            {msg.from === 'novah' && msg.widget === 'content-type' && step === 'content-type' && (
              <WidgetWrap>
                <StyleGrid>
                  {CONTENT_TYPE_OPTIONS.map((opt) => (
                    <StyleOption key={opt.id} $active={false} onClick={() => confirmContentType(opt.id)}>
                      <StyleIcon>{opt.icon}</StyleIcon>
                      <StyleLabel>{opt.label}</StyleLabel>
                      <PlanTag>{opt.hint}</PlanTag>
                    </StyleOption>
                  ))}
                </StyleGrid>
              </WidgetWrap>
            )}

            {msg.from === 'novah' && msg.widget === 'style' && step === 'style' && (
              <WidgetWrap>
                <StyleGrid>
                  {/* Solid colors — Free+ */}
                  <StyleOption $active={false} onClick={() => confirmStyle('pastel')}>
                    <StyleIcon>🎨</StyleIcon><StyleLabel>Solid colors</StyleLabel>
                    <PlanTag>Free</PlanTag>
                  </StyleOption>

                  {/* Stock photos — Pro+ */}
                  <StyleOption
                    $active={false}
                    $locked={userPlan === 'free'}
                    onClick={() => userPlan === 'free' ? onNavigateBilling() : confirmStyle('stock')}
                  >
                    {userPlan === 'free' && <LockBadge>🔒</LockBadge>}
                    <StyleIcon>📷</StyleIcon><StyleLabel>Stock photos</StyleLabel>
                    <PlanTag>Pro</PlanTag>
                  </StyleOption>

                  {/* My photos — Business only */}
                  {hasBackgroundImages && (
                    <StyleOption
                      $active={false}
                      $locked={userPlan !== 'business'}
                      onClick={() => userPlan !== 'business' ? onNavigateBilling() : confirmStyle('asset')}
                    >
                      {userPlan !== 'business' && <LockBadge>🔒</LockBadge>}
                      <StyleIcon>🖼️</StyleIcon><StyleLabel>My photos</StyleLabel>
                      <PlanTag>Business</PlanTag>
                    </StyleOption>
                  )}

                  {/* Mix it up — Pro+ */}
                  <StyleOption
                    $active={false}
                    $locked={userPlan === 'free'}
                    onClick={() => userPlan === 'free' ? onNavigateBilling() : confirmStyle('mix')}
                  >
                    {userPlan === 'free' && <LockBadge>🔒</LockBadge>}
                    <StyleIcon>✨</StyleIcon><StyleLabel>Mix it up</StyleLabel>
                    <PlanTag>Pro</PlanTag>
                  </StyleOption>

                  {/* AI Generated — Business only */}
                  <StyleOption
                    $active={false}
                    $locked={userPlan !== 'business'}
                    onClick={() => userPlan !== 'business' ? onNavigateBilling() : confirmStyle('ai')}
                  >
                    {userPlan !== 'business' && <LockBadge>🔒</LockBadge>}
                    <StyleIcon>🤖</StyleIcon><StyleLabel>AI Generated</StyleLabel>
                    <PlanTag>Business</PlanTag>
                  </StyleOption>
                </StyleGrid>
              </WidgetWrap>
            )}

            {msg.from === 'novah' && msg.widget === 'ai-model' && step === 'ai-model' && (
              <WidgetWrap>
                <StyleGrid>
                  {KIE_MODELS.map((m) => (
                    <StyleOption key={m.id} $active={false} onClick={() => confirmAiModel(m.id)}>
                      <StyleIcon>🤖</StyleIcon><StyleLabel>{m.name}</StyleLabel>
                    </StyleOption>
                  ))}
                  <StyleOption $active={false} onClick={() => confirmAiModel('mix')} style={{ gridColumn: '1 / -1' }}>
                    <StyleIcon>🎲</StyleIcon><StyleLabel>Mix all models</StyleLabel>
                    <PlanTag>Equal split</PlanTag>
                  </StyleOption>
                </StyleGrid>
              </WidgetWrap>
            )}

            {msg.from === 'novah' && msg.widget === 'confirm' && step === 'confirm' && (
              <WidgetWrap>
                <SummaryCard>
                  {Array.from(selectedPlatforms).map((p) => {
                    const info = PLATFORMS.find((x) => x.id === p);
                    return <div key={p}>{info?.icon} <strong>{countPerPlatform}</strong> {info?.label} posts</div>;
                  })}
                  <div>🖼️ {imageStyle === 'mix' ? 'Stock + Solid mix' : imageStyle === 'stock' ? 'Stock photos' : imageStyle === 'pastel' ? 'Solid colors' : imageStyle === 'ai' ? 'AI Generated' : 'My photos'}</div>
                  <div>📋 {CONTENT_TYPE_OPTIONS.find(o => o.id === contentType)?.label || 'Mix'}</div>
                  {customPrompt && <div>💡 "{customPrompt}"</div>}
                  <div style={{ marginTop: 4 }}>💰 <strong>{totalPosts} credits</strong> (you have {credits})</div>
                </SummaryCard>
                {credits < totalPosts ? (
                  <ConfirmBtn disabled>Not enough credits</ConfirmBtn>
                ) : (
                  <ConfirmBtn onClick={triggerGenerate}>
                    Generate {totalPosts} Posts
                  </ConfirmBtn>
                )}
                <SkipBtn onClick={resetFlow}>Change something</SkipBtn>
              </WidgetWrap>
            )}

            {msg.from === 'novah' && msg.widget === 'done' && step === 'done' && (
              <WidgetWrap>
                <QualityRow>
                  <QualityLabel htmlFor="export-quality">Download size</QualityLabel>
                  <QualitySelect
                    id="export-quality"
                    value={exportQuality}
                    onChange={(e) => setExportQuality(e.target.value as ExportQuality)}
                  >
                    <option value="mobile">{EXPORT_PRESETS.mobile.label}</option>
                    <option value="low">{EXPORT_PRESETS.low.label}</option>
                    <option value="original">{EXPORT_PRESETS.original.label}</option>
                  </QualitySelect>
                </QualityRow>
                <ActionRow>
                  <ConfirmBtn onClick={() => onDownloadZIP(exportQuality)}>Download ZIP</ConfirmBtn>
                  <OutlineBtn onClick={() => onExportCSV(exportQuality)}>Export CSV</OutlineBtn>
                </ActionRow>
                <SkipBtn onClick={resetFlow} style={{ marginTop: 6 }}>Generate more</SkipBtn>
              </WidgetWrap>
            )}
          </div>
        ))}

        {typing && (
          <MsgRow $from="novah">
            <Avatar><img src="/novah_laugh.png" alt="Novah" /></Avatar>
            <Bubble $from="novah"><TypingDots><span /><span /><span /></TypingDots></Bubble>
          </MsgRow>
        )}

        {step === 'generating' && (
          <WidgetWrap>
            <MsgRow $from="novah">
              <Avatar><img src="/novah_laugh.png" alt="Novah" /></Avatar>
              <Bubble $from="novah">
                <div style={{ fontSize: '13px', marginBottom: 6, opacity: 0.85, fontStyle: 'italic' }}>
                  {GEN_STATUS_MESSAGES[genStatusIdx]}
                </div>
                <div style={{ fontWeight: 600 }}>
                  {streamProgress.current}/{streamProgress.total} posts
                </div>
                <ProgressBar>
                  <ProgressFill $pct={streamProgress.total ? (streamProgress.current / streamProgress.total) * 100 : 0} />
                </ProgressBar>
              </Bubble>
            </MsgRow>
          </WidgetWrap>
        )}

        {stepHistory.length > 0 && !['generating', 'done'].includes(step) && (
          <BackBtn onClick={goBack}>&larr; Go back</BackBtn>
        )}
        <div ref={scrollRef} />
      </ChatContainer>
    </div>
  );
}
